import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Magazzino from "@/pages/Magazzino";
import Dipendenti from "@/pages/Dipendenti";
import ArchivioDipendenti from "@/pages/ArchivioDipendenti";
import RegistroAttivita from "@/pages/RegistroAttivita";
import Utenti from "@/pages/Utenti";
import Cantieri from "@/pages/Cantieri";
import CantiereDetail from "@/pages/CantiereDetail";
import Localizzazione from "@/pages/Localizzazione";
import GiornataMezzi from "@/pages/GiornataMezzi";
import Segnalazioni from "@/pages/Segnalazioni";
import ReportisticaSicurezza from "@/pages/ReportisticaSicurezza";
import VerificheCantiere from "@/pages/VerificheCantiere";
import PianificazioneGriglia from "@/pages/PianificazioneGriglia";
import Consegna from "@/pages/Consegna";
import Storico from "@/pages/Storico";
import Reportistica from "@/pages/Reportistica";
import Discrepanze from "@/pages/Discrepanze";
import NomineSicurezza from "@/pages/NomineSicurezza";
import Reperibilita from "@/pages/Reperibilita";
import Monitor from "@/pages/Monitor";
import MagazzinoMateriali from "@/pages/MagazzinoMateriali";
import MovimentiMateriali from "@/pages/MovimentiMateriali";
import MaterialePubblico from "@/pages/MaterialePubblico";
import Fascicolo from "@/pages/Fascicolo";
import Attrezzature from "@/pages/Attrezzature";
import CostiManutenzione from "@/pages/CostiManutenzione";
import FeriePermessi from "@/pages/FeriePermessi";
import DocumentiAziendali from "@/pages/DocumentiAziendali";
import ArchivioAttrezzature from "@/pages/ArchivioAttrezzature";
import AttrezzaturaDetail from "@/pages/AttrezzaturaDetail";
import AttrezzaturaPubblica from "@/pages/AttrezzaturaPubblica";
import MagazzinoPubblico from "@/pages/MagazzinoPubblico";
import CantierePubblico from "@/pages/CantierePubblico";
import SistemiIntegrati from "@/pages/SistemiIntegrati";
import Infortuni from "@/pages/Infortuni";
import Cassette from "@/pages/Cassette";
import Manuali from "@/pages/Manuali";
import Scadenzario from "@/pages/Scadenzario";
import PosAppalto from "@/pages/PosAppalto";
import Preventivi from "@/pages/Preventivi";
import DDT from "@/pages/DDT";
import Protocollo from "@/pages/Protocollo";
import Lavagna from "@/pages/Lavagna";
import Contatori from "@/pages/Contatori";
import Tachigrafo from "@/pages/Tachigrafo";
import AreaOperativa from "@/pages/AreaOperativa";
import VisitaOspite from "@/components/VisitaOspite";
import PaginaVisita from "@/pages/PaginaVisita";
import DocumentiVisita from "@/pages/DocumentiVisita";
import Formatore from "@/pages/Formatore";
import TestFormazione from "@/pages/TestFormazione";
import { puoGestire } from "@/lib/ruoli";

function Protected({ children, adminOnly = false, gestione = false }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-slate-500">Caricamento...</div>;
  // Dove stava andando se lo porta dietro fino al login: chi inquadra
  // l'etichetta di uno scaffale con la sessione scaduta deve ritrovarsi sul
  // prelievo di quell'articolo, non sulla dashboard.
  if (!user) return <Navigate to="/login" replace state={{ da: location.pathname + location.search }} />;
  // Lo schermo del capannone non esce dalla sua pagina: e' appeso al muro,
  // sempre connesso e alla portata di chiunque passi di li'.
  if (user.role === "monitor") return <Navigate to="/monitor" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  // Gli archivi - ex dipendenti, macchine dismesse - sono elenchi da
  // consultare, non operazioni definitive: li apre anche chi gestisce.
  if (gestione && !puoGestire(user)) return <Navigate to="/" replace />;
  return children;
}

// I sistemi di gestione li seguono direzione, amministrazione e tecnici: gli
// altri ruoli non ci entrano nemmeno digitando l'indirizzo a mano.
function SoloSistemi({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-slate-500">Caricamento...</div>;
  if (!["admin", "amministrativo", "tecnico"].includes(user?.role)) return <Navigate to="/" replace />;
  return children;
}

function SoloMonitor({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen bg-slate-950" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!["monitor", "admin", "tecnico"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        {/* Sta fuori dalle rotte perche' la barra della visita deve comparire
            anche sulle pagine pubbliche dei QR, che non passano dal menu. */}
        <VisitaOspite />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/visita" element={<PaginaVisita />} />
          {/* La casa di chi e' in visita: lo scan in grande e i documenti
              da consultare. Sta fuori dal menu dell'app perche' chi la
              apre un account non ce l'ha. */}
          <Route path="/visita/documenti" element={<DocumentiVisita />} />
          <Route path="/fascicolo/:id" element={<Fascicolo />} />
          {/* Il test in aula: lo aprono i partecipanti dal loro telefono,
              senza account, con il codice proiettato. */}
          <Route path="/test" element={<TestFormazione />} />
          <Route path="/test/:codice" element={<TestFormazione />} />
          <Route path="/attrezzatura/:id" element={<AttrezzaturaPubblica />} />
          <Route path="/magazzino-pubblico/:id" element={<MagazzinoPubblico />} />
          <Route path="/materiale/:id" element={<MaterialePubblico />} />
          <Route path="/cantiere-pubblico/:id" element={<CantierePubblico />} />
          <Route path="/monitor" element={<SoloMonitor><Monitor /></SoloMonitor>} />
          <Route element={<Protected><AppLayout /></Protected>}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/consegna" element={<Consegna />} />
            <Route path="/storico" element={<Storico />} />
            <Route path="/magazzino" element={<Magazzino />} />
            <Route path="/magazzino-materiali" element={<MagazzinoMateriali />} />
            <Route path="/movimenti-materiali" element={<MovimentiMateriali />} />
            <Route path="/dipendenti" element={<Dipendenti />} />
            <Route path="/archivio-dipendenti" element={<Protected gestione><ArchivioDipendenti /></Protected>} />
            <Route path="/registro" element={<Protected adminOnly><RegistroAttivita /></Protected>} />
            <Route path="/utenti" element={<Protected adminOnly><Utenti /></Protected>} />
            <Route path="/cantieri" element={<Cantieri />} />
            <Route path="/cantieri/:id" element={<CantiereDetail />} />
            <Route path="/localizzazione" element={<Localizzazione />} />
            <Route path="/giornata-mezzi" element={<Protected adminOnly><GiornataMezzi /></Protected>} />
            <Route path="/segnalazioni" element={<Segnalazioni />} />
            <Route path="/reportistica-sicurezza" element={<ReportisticaSicurezza />} />
            <Route path="/verifiche-cantiere" element={<VerificheCantiere />} />
            <Route path="/pianificazione-griglia" element={<PianificazioneGriglia />} />
            <Route path="/attrezzature" element={<Attrezzature />} />
            <Route path="/cassette" element={<Cassette />} />
            <Route path="/manuali" element={<Manuali />} />
            <Route path="/scadenzario" element={<Scadenzario />} />
            <Route path="/formatore" element={<Protected adminOnly><Formatore /></Protected>} />
            <Route path="/contatori" element={<Contatori />} />
            <Route path="/area-operativa" element={<AreaOperativa />} />
            <Route path="/tachigrafo" element={<Tachigrafo />} />
            <Route path="/costi-manutenzione" element={<Protected adminOnly={false}><CostiManutenzione /></Protected>} />
            <Route path="/ferie-permessi" element={<FeriePermessi />} />
            <Route path="/documenti-aziendali" element={<DocumentiAziendali />} />
            <Route path="/archivio-attrezzature" element={<Protected gestione><ArchivioAttrezzature /></Protected>} />
            <Route path="/attrezzature/:id" element={<AttrezzaturaDetail />} />
            <Route path="/preventivi" element={<Protected adminOnly><Preventivi /></Protected>} />
            <Route path="/pos-appalto" element={<Protected adminOnly><PosAppalto /></Protected>} />
            <Route path="/ddt" element={<Protected><DDT /></Protected>} />
            <Route path="/protocollo" element={<Protected><Protocollo /></Protected>} />
            {/* La lavagna sta su una pagina sua, non dentro una finestra:
                con un dialogo aperto Radix spegne i tocchi su tutta la
                pagina, e su una tela da disegno è esattamente il male. */}
            <Route path="/lavagna/:lavId" element={<Protected><Lavagna /></Protected>} />
            <Route path="/reportistica" element={<Protected adminOnly><Reportistica /></Protected>} />
            <Route path="/discrepanze" element={<Protected adminOnly><Discrepanze /></Protected>} />
            <Route path="/nomine-sicurezza" element={<NomineSicurezza />} />
            <Route path="/reperibilita" element={<Reperibilita />} />
            <Route path="/sistemi-integrati" element={<SoloSistemi><SistemiIntegrati /></SoloSistemi>} />
            <Route path="/infortuni" element={<SoloSistemi><Infortuni /></SoloSistemi>} />
          </Route>
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
